import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const useSafeInView = (margin = "-40px") => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin });
  const [forced, setForced] = useState(false);

  useEffect(() => {
    if (inView) return undefined;
    const check = () => {
      const el = ref.current;
      if (el && el.getBoundingClientRect().top < window.innerHeight - 24) setForced(true);
    };
    const t = setTimeout(check, 900);
    window.addEventListener("scroll", check, { passive: true });
    check();
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", check);
    };
  }, [inView]);

  return [ref, inView || forced];
};

export const Reveal = ({ children, delay = 0, y = 36, className = "", as = "div", ...rest }) => {
  const [ref, visible] = useSafeInView();
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`-mb-[0.18em] block overflow-hidden pb-[0.18em] -ml-[0.06em] pl-[0.06em] ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 ${className}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-volt" />
    {children}
  </span>
);
